
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/seo/SEOHead';
import JsonLd from '../../components/seo/JsonLd';

const AboutPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "GCM Mobile Notary",
    "jobTitle": "Notary Public",
    "worksFor": {
      "@type": "LocalBusiness",
      "name": "GCM Mobile Notary"
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "5103 Longridge Ave",
      "addressLocality": "Sherman Oaks",
      "addressRegion": "CA",
      "postalCode": "91423",
      "addressCountry": "US"
    },
    "telephone": "+18772113011",
    "email": "office@notaryshermanoaks.com"
  };

  const certifications = [
    'California State Certified Notary Public',
    'National Notary Association Member',
    'Liability Insurance Coverage',
    'Background Checked & Bonded'
  ];

  const experience = [
    {
      icon: 'ri-calendar-line',
      title: '10+ Years Experience',
      description: 'Over a decade of professional notary services'
    },
    {
      icon: 'ri-user-heart-line',
      title: '5,000+ Clients Served',
      description: 'Thousands of satisfied customers throughout the valley'
    },
    {
      icon: 'ri-time-line',
      title: 'Same-Day Service',
      description: 'Emergency and urgent appointments available'
    },
    {
      icon: 'ri-shield-check-line',
      title: '100% Reliable',
      description: 'Professional, punctual, and trustworthy service'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="About GCM Mobile Notary - Licensed California Notary Public in Sherman Oaks"
        description="Learn about our experienced mobile notary public serving Sherman Oaks and San Fernando Valley. Over 10 years experience, licensed, insured, and trusted by thousands of clients."
        keywords="About Mobile Notary Sherman Oaks, Licensed Notary Public California, Experienced Notary Services, San Fernando Valley Notary Background"
        canonical={`${siteUrl}/about`}
        ogTitle="About GCM Mobile Notary - Licensed California Notary Public in Sherman Oaks"
        ogDescription="Learn about our experienced mobile notary public serving Sherman Oaks and San Fernando Valley. Over 10 years experience, licensed, insured, and trusted by thousands of clients."
        ogUrl={`${siteUrl}/about`}
        ogImage={`${siteUrl}/og-image.jpg`}
      />
      <JsonLd data={personSchema} />
      
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                About GCM Mobile Notary
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Your trusted mobile notary public serving Sherman Oaks and the entire San Fernando Valley 
                with professional, reliable, and convenient notarization services.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="tel:+18772113011"
                  className="bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors text-center whitespace-nowrap cursor-pointer"
                >
                  Call (877) 211-3011
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/1f9594657ba5223e02e20435290ae399.jpg"
                alt="Professional notary public"
                className="rounded-xl shadow-lg object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              My Story &amp; Commitment
            </h2>
          </div>
          
          <div className="prose prose-lg mx-auto text-gray-600">
            <p className="text-xl leading-relaxed mb-6">
              Welcome! I'm a California state-certified notary public with over 10 years of experience 
              serving the Sherman Oaks community and the entire San Fernando Valley. My passion for 
              helping people with their important legal documents led me to specialize in mobile notary services.
            </p>
            
            <p className="text-lg leading-relaxed mb-6">
              I understand that your time is valuable, and traveling to a notary office can be inconvenient, 
              especially when dealing with urgent documents or mobility challenges. That's why I bring 
              professional notary services directly to your location – whether it's your home, office, 
              hospital, or any other convenient meeting place.
            </p>
            
            <p className="text-lg leading-relaxed mb-6">
              My expertise spans a wide range of document types, from Power of Attorney and real estate 
              transactions to living trusts, wills, and apostille services. I take pride in ensuring 
              every notarization is performed accurately, legally, and with the highest level of 
              professionalism.
            </p>
            
            <p className="text-lg leading-relaxed mb-6">
              Operating from my office at 5103 Longridge Ave. in Sherman Oaks, I'm committed to serving 
              our local community with integrity, reliability, and exceptional customer service. Your 
              important documents deserve careful attention, and I'm here to make the notarization process 
              as smooth and stress-free as possible.
            </p>

            <div className="bg-orange-50 p-6 rounded-lg mt-8">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Contact Information</h3>
              <div className="space-y-2">
                <div className="flex items-center space-x-3">
                  <i className="ri-map-pin-line text-orange-500"></i>
                  <span>5103 Longridge Ave. Sherman Oaks, CA 91423</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-phone-line text-orange-500"></i>
                  <span>(877) 211-3011</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-mail-line text-orange-500"></i>
                  <span>office@notaryshermanoaks.com</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Stats */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Experience &amp; Expertise
            </h2>
            <p className="text-xl text-gray-600">
              Trusted by thousands of clients throughout the San Fernando Valley
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {experience.map((item, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${item.icon} text-2xl text-orange-500`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Licensed &amp; Certified Professional
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                I maintain all required certifications and insurance to provide you with 
                secure, legal, and professional notary services throughout California.
              </p>
              
              <div className="space-y-4">
                {certifications.map((cert, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-orange-500 text-sm"></i>
                    </div>
                    <span className="text-gray-700 font-medium">{cert}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20notary%20certification%20documents%20and%20official%20notary%20seal%20stamp%20on%20modern%20desk%2C%20legal%20credentials%20display%2C%20California%20state%20notary%20commission%20certificate%2C%20professional%20office%20setting%2C%20clean%20organized%20workspace&width=600&height=500&seq=certifications&orientation=landscape"
                alt="Notary certifications and credentials"
                className="rounded-xl shadow-lg object-cover w-full h-80"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              My Core Values
            </h2>
            <p className="text-xl text-gray-600">
              The principles that guide every client interaction
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-2xl text-orange-500"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Integrity</h3>
              <p className="text-gray-600">
                Every document is handled with the highest ethical standards and complete confidentiality.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-time-line text-2xl text-orange-500"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Reliability</h3>
              <p className="text-gray-600">
                Punctual, professional service you can count on for all your notarization needs.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-customer-service-line text-2xl text-orange-500"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Service Excellence</h3>
              <p className="text-gray-600">
                Dedicated to making your notarization experience convenient, efficient, and stress-free.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Work Together?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Experience the convenience of professional mobile notary services. 
            I'm here to help with all your document notarization needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+18772113011"
              className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call (877) 211-3011
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
